import api from './api'
import {
  SellerQueueItem,
  SellerServiceSessionItem,
  SellerServiceNoSaleReasonItem,
  NoSaleReasonCodeOption,
} from '../contexts/SellerQueueContext'

/**
 * Service layer for Seller Queue operations
 * Interfaces with backend at /seller-queue, /seller-service-session, /seller-service-no-sale-reason
 */

// ============ SELLER QUEUE API ============

/**
 * Get queue entry for current seller in pluginspace
 */
export const getQueueByPluginspace = async (pluginspaceId: number): Promise<SellerQueueItem[]> => {
  try {
    const response = await api.get(`/api/seller-queue/pluginspace?pluginspaceId=${pluginspaceId}`)
    // Backend returns { data: [...], success, typemsg }, extract the data array
    const queueArray = response.data?.data || response.data || []
    return Array.isArray(queueArray) ? queueArray : []
  } catch (error) {
    console.error('[sellerQueueService] getQueueByPluginspace - ERROR:', error)
    throw error
  }
}

/**
 * Get queue entries filtered by status
 */
export const getQueueByStatus = async (status: string, pluginspaceId: number): Promise<SellerQueueItem[]> => {
  try {
    const response = await api.get(`/api/seller-queue/status/${status}?pluginspaceId=${pluginspaceId}`)
    const queueArray = response.data?.data || response.data || []
    return Array.isArray(queueArray) ? queueArray : []
  } catch (error) {
    throw error
  }
}

/**
 * Get next available seller in queue
 */
export const getNextAvailable = async (pluginspaceId: number): Promise<SellerQueueItem | null> => {
  try {
    const response = await api.get(`/api/seller-queue/next-available?pluginspaceId=${pluginspaceId}`)
    // Backend might wrap in { data: ..., success, typemsg }
    return response.data?.data || response.data
  } catch (error) {
    throw error
  }
}

/**
 * Create queue entry for seller
 * Called when seller joins queue (manual or automatic on login)
 */
export const createQueueEntry = async (
  pluginspaceId: number,
  sellerId: number,
  position?: number
): Promise<SellerQueueItem> => {
  try {
    const response = await api.post('/api/seller-queue/createQueueEntry', {
      pluginspaceId,
      sellerId,
      ...(position && position > 0 ? { position } : {}),
    })
    // Backend wraps response: { createSellerQueue: {...}, success, typemsg }
    return response.data?.createSellerQueue || response.data
  } catch (error) {
    console.error('[sellerQueueService] createQueueEntry - ERROR:', error)
    throw error
  }
}

/**
 * Update queue status (available, paused, in_service, offline)
 */
export const updateQueueStatus = async (id: number, status: string): Promise<void> => {
  try {
    await api.put(`/api/seller-queue/${id}/status`, { status })
  } catch (error) {
    console.error('[sellerQueueService] updateQueueStatus - ERROR:', error)
    throw error
  }
}

/**
 * Remove seller from queue
 */
export const removeFromQueue = async (sellerId: number): Promise<void> => {
  try {
    await api.delete(`/api/seller-queue/seller/${sellerId}`)
  } catch (error) {
    console.error('[sellerQueueService] removeFromQueue - ERROR:', error)
    throw error
  }
}

export const closeSellerAttendance = async (
  sessionId: number,
  sellerId: number,
  outcome: 'sale_completed' | 'no_sale',
  reasonCode?: number,
  note?: string,
  saleAmount?: number
): Promise<void> => {
  try {
    await api.post('/api/seller-queue/close-attendance', {
      sessionId,
      sellerId,
      outcome,
      reasonCode,
      note,
      saleAmount,
    })
  } catch (error) {
    console.error('[sellerQueueService] closeSellerAttendance - ERROR:', error)
    throw error
  }
}

export const returnSellerToQueue = async (
  sessionId: number,
  sellerId: number,
  outcome: 'sale_completed' | 'no_sale',
  reasonCode?: number,
  note?: string,
  saleAmount?: number
): Promise<void> => {
  try {
    await api.post('/api/seller-queue/return-to-queue', {
      sessionId,
      sellerId,
      outcome,
      reasonCode,
      note,
      saleAmount,
    })
  } catch (error) {
    console.error('[sellerQueueService] returnSellerToQueue - ERROR:', error)
    throw error
  }
}

export const returnSellerToQueueSeller = async (
  sessionId: number,
  outcome: 'sale_completed' | 'no_sale',
  reasonCode?: number,
  note?: string,
  saleAmount?: number
): Promise<void> => {
  try {
    await api.post('/api/seller-queue/return-to-queue-seller', {
      sessionId,
      outcome,
      reasonCode,
      note,
      saleAmount,
    })
  } catch (error) {
    console.error('[sellerQueueService] returnSellerToQueueSeller - ERROR:', error)
    throw error
  }
}

export const startSellerAttendance = async (
  sellerId: number,
  queuePositionAtStart?: number
): Promise<void> => {
  try {
    await api.post('/api/seller-queue/start-attendance', {
      sellerId,
      queuePositionAtStart,
    })
  } catch (error) {
    console.error('[sellerQueueService] startSellerAttendance - ERROR:', error)
    throw error
  }
}

/**
 * Update lastCalledAt timestamp
 */
export const updateLastCalledAt = async (id: number): Promise<void> => {
  try {
    await api.put(`/api/seller-queue/${id}/last-called`, {})
  } catch (error) {
    throw error
  }
}

/**
 * Update lastFinishedAt timestamp
 */
export const updateLastFinishedAt = async (id: number): Promise<void> => {
  try {
    await api.put(`/api/seller-queue/${id}/last-finished`, {})
  } catch (error) {
    throw error
  }
}

// ============ SELLER SERVICE SESSION API ============

/**
 * Get current active session for seller
 */
export const getCurrentSession = async (
  pluginspaceId: number,
  sellerId: number
): Promise<SellerServiceSessionItem | null> => {
  try {
    const response = await api.get(`/api/seller-service-session/current?pluginspaceId=${pluginspaceId}&sellerId=${sellerId}`)
    // Unwrap response wrapper if needed
    return response.data?.data || response.data
  } catch (error) {
    // Not finding a session is normal (no active session)
    if ((error as any)?.response?.status === 404) {
      return null
    }
    throw error
  }
}

/**
 * Get session history for seller/pluginspace
 */
export const getSessionHistory = async (
  pluginspaceId: number,
  startDate?: string,
  endDate?: string
): Promise<SellerServiceSessionItem[]> => {
  try {
    let url = `/api/seller-service-session/pluginspace/${pluginspaceId}`
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`
    }
    const response = await api.get(url)
    const sessionArray = response.data?.data || response.data || []
    return Array.isArray(sessionArray) ? sessionArray : []
  } catch (error) {
    throw error
  }
}

/**
 * Get specific session by ID
 */
export const getSessionById = async (sessionId: number): Promise<SellerServiceSessionItem> => {
  try {
    const response = await api.get(`/api/seller-service-session/${sessionId}`)
    // Unwrap response wrapper if needed
    return response.data?.data || response.data
  } catch (error) {
    throw error
  }
}

/**
 * Complete session with outcome
 * Called when seller finishes attending customer
 */
export const completeSession = async (
  sessionId: number,
  outcome: string,
  durationSeconds: number
): Promise<void> => {
  try {
    await api.put(`/api/seller-service-session/${sessionId}`, { outcome, durationSeconds })
  } catch (error) {
    console.error('[sellerQueueService] completeSession - ERROR:', error)
    throw error
  }
}

/**
 * Update session status (waiting, in_progress, finished)
 */
export const updateSessionStatus = async (sessionId: number, status: string): Promise<void> => {
  try {
    await api.put(`/api/seller-service-session/${sessionId}`, { status })
  } catch (error) {
    throw error
  }
}

// ============ NO SALE REASONS API ============

/**
 * Get no-sale reasons for specific session
 */
export const getNoSaleReasonsBySession = async (
  sessionId: number
): Promise<SellerServiceNoSaleReasonItem[]> => {
  try {
    const response = await api.get(`/api/seller-service-no-sale-reason/session/${sessionId}`)
    return response.data || []
  } catch (error) {
    throw error
  }
}

/**
 * Create no-sale reason record
 * Called when customer didn't make purchase
 */
export const createNoSaleReason = async (
  sessionId: number,
  reasonCode: number,
  productName?: string,
  sizeWanted?: string,
  colorWanted?: string,
  note?: string
): Promise<SellerServiceNoSaleReasonItem> => {
  try {
    const response = await api.post('/api/seller-service-no-sale-reason', {
      sessionId,
      reasonCode,
      productName,
      sizeWanted,
      colorWanted,
      note,
    })
    return response.data
  } catch (error) {
    console.error('[sellerQueueService] createNoSaleReason - ERROR:', error)
    throw error
  }
}

export const getNoSaleReasonCodes = async (): Promise<NoSaleReasonCodeOption[]> => {
  try {
    const response = await api.get('/api/seller-service-no-sale-reason/reason-codes')
    const payload = response.data
    const reasonCodes =
      payload?.data ??
      payload?.result ??
      payload?.items ??
      payload

    return Array.isArray(reasonCodes) ? reasonCodes : []
  } catch (error) {
    console.error('[sellerQueueService] getNoSaleReasonCodes - ERROR:', error)
    throw error
  }
}
